<template>
  <div class="signup-component">
    <!-- Step 1: User Information -->
    <div v-if="currentStep === 'user'" key="user" class="signup-step">
      <div class="mb-6">
        <span>Step-1</span>
      </div>
      <div class="mb-6">
        <h4 class="font-bold text-2xl mb-2">Hello User</h4>
        <p>Tell us a little bit about you</p>
      </div>

      <v-form ref="userForm">
        <div class="flex flex-col sm:flex-row justify-between gap-4">
          <v-text-field
            v-model="form.firstName"
            outlined
            label="First Name *"
            placeholder="First Name *"
            :rules="[rules.required]"
            @keyup.enter="validateUserAndProceed"
          ></v-text-field>
          <v-text-field
            v-model="form.lastName"
            outlined
            label="Last Name *"
            placeholder="Last Name *"
            :rules="[rules.required]"
            @keyup.enter="validateUserAndProceed"
          ></v-text-field>
        </div>

        <v-text-field
          v-model="form.mobile"
          outlined
          label="Mobile Number *"
          placeholder="Enter 10-digit mobile number"
          @input="debouncedCheckMobileAvailability"
          :rules="[rules.required, rules.mobile]"
          @keyup.enter="validateUserAndProceed"
        >
          <template v-slot:append v-if="mobileChecking">
            <Loader :size="3" />
          </template>
          <template v-slot:append v-else-if="mobileChecked && !mobileAvailable">
            <v-icon color="error" small>mdi-close-circle</v-icon>
          </template>
          <template v-slot:append v-else-if="mobileChecked && mobileAvailable">
            <v-icon color="success" small>mdi-check-circle</v-icon>
          </template>
        </v-text-field>

        <v-text-field
          v-model="form.email"
          outlined
          label="Email ID"
          placeholder="Enter your email address"
          @keyup.enter="validateUserAndProceed"
        ></v-text-field>

        <!-- <v-text-field
          v-model="form.employeeCode"
          outlined
          disabled
          label="Employee Code *"
          placeholder="Enter your employee code"
          :rules="[rules.required]"
        ></v-text-field> -->

        <div class="d-flex justify-end">
          <!-- <v-btn color="secondary" @click="currentStep = 'company'" outlined>
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn> -->
          <v-btn
            color="primary"
            @click="validateUserAndProceed"
            :loading="isLoading"
            :disabled="!isUserFormValid || !mobileAvailable"
            depressed
            :block="$vuetify.breakpoint.smAndDown"
          >
            Continue
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <div v-if="mobileChecked && !mobileAvailable" class="text-caption error--text mb-2">
          This mobile number is already registered. Please try another.
        </div>
      </v-form>
      <GoogleLogin @google-login-success="handleGoogleLoginSuccess" :show-guest-login="false" />
    </div>
    <!-- Step 2: Company Information -->
    <div v-if="currentStep === 'company'" key="company" class="signup-step">
      <div class="mb-6">
        <v-icon class="mr-2" size="30" @click="currentStep = 'user'">mdi-chevron-left</v-icon>
        <span>Step-2</span>
      </div>
      <div class="mb-6">
        <h4 class="font-bold text-2xl mb-2" v-if="googleSignupData">
          Hello {{ googleSignupData.name }}
        </h4>
        <h4 class="font-bold text-2xl mb-2" v-else>Let us get to know you better</h4>
        <p>Few more questions and we are ready to fly</p>
      </div>

      <v-form ref="companyForm">
        <v-text-field
          v-model="form.companyName"
          outlined
          label="Company Name *"
          placeholder="Enter your company name"
          :rules="[rules.required]"
          @input="handleCompanyNameInput"
          @keyup.enter="validateCompanyAndNext"
        >
          <template v-slot:append v-if="orgNameChecking">
            <Loader :size="3" />
          </template>
          <template v-slot:append v-else-if="orgNameChecked && !orgNameAvailable">
            <v-icon color="error" small>mdi-close-circle</v-icon>
          </template>
          <template v-slot:append v-else-if="orgNameChecked && orgNameAvailable">
            <v-icon color="success" small>mdi-check-circle</v-icon>
          </template>
        </v-text-field>

        <v-text-field
          v-model="form.companyCode"
          outlined
          label="Company Code *"
          placeholder="Enter 6-character uppercase code"
          maxlength="6"
          @input="handleCompanyCodeInput"
          :rules="[rules.required, rules.companyCode]"
          @keyup.enter="validateCompanyAndNext"
        >
          <template v-slot:append-inner>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey lighten-1"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span>6-character uppercase code (letters only, e.g. ABCDEF)</span>
            </v-tooltip>
          </template>
          <template v-slot:append v-if="orgChecking">
            <Loader :size="3" />
          </template>
          <template v-slot:append v-else-if="orgChecked && !orgAvailable">
            <v-icon color="error" small>mdi-close-circle</v-icon>
          </template>
          <template v-slot:append v-else-if="orgChecked && orgAvailable">
            <v-icon color="success" small>mdi-check-circle</v-icon>
          </template>
        </v-text-field>
        <div class="mb-4">
          <p class="text-grey-darken-1 mb-2">Number of employees</p>
          <v-chip
            v-for="(size, index) in companySizes"
            :key="index"
            :outlined="form.companySize !== size"
            :color="form.companySize === size ? 'primary' : 'default'"
            @click="form.companySize = size"
            class="mr-6 mb-2"
            large
            label
          >
            {{ size }}
          </v-chip>
        </div>
        <v-select
          v-model="form.useCase"
          :items="useCases"
          outlined
          label="What problem are you looking to solve?(Select at least 2) *"
          :rules="[rules.required]"
          item-text="text"
          item-value="value"
          multiple
          @change="handleUseCaseChange"
        >
          <template v-slot:item="{ item, attrs, on }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-action>
                <v-checkbox
                  :input-value="attrs.inputValue"
                  :disabled="item.value === 'hrms'"
                ></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption grey--text">{{
                  item.description
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
        <div class="d-flex justify-end">
          <v-btn
            color="primary"
            @click="validateCompanyAndNext"
            :disabled="!isCompanyFormValid || !orgAvailable"
            :loading="isLoading"
            depressed
            :block="$vuetify.breakpoint.smAndDown"
          >
            {{ googleSignupData?.googleToken ? 'Submit' : 'Continue' }}
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <div v-if="orgChecked && !orgAvailable" class="text-caption error--text mb-2">
          This organization code is already taken. Please try another.
        </div>
        <div v-if="orgNameChecked && !orgNameAvailable" class="text-caption error--text mb-2">
          This organization name is already taken. Please try another.
        </div>
      </v-form>
    </div>

    <!-- Step 3: Select Plan -->
    <div v-if="currentStep === 'plan-selection'" key="plan-selection" class="signup-step">
      <div class="mb-6">
        <v-icon class="mr-2" size="30" @click="currentStep = 'company'">mdi-chevron-left</v-icon>
        <span>Step-3</span>
      </div>
      <div class="mb-6">
        <h4 class="font-bold text-2xl mb-2">Select Plan</h4>
        <p class="text-grey-darken-1">Select a plan that suits your needs</p>
      </div>

      <v-row class="mb-4">
        <v-col cols="12" sm="6" v-for="plan in plans" :key="plan.name" class="d-flex">
          <v-card
            class="flex-grow-1 rounded-lg transition-all d-flex flex-column align-center justify-center py-6 px-4 cursor-pointer"
            :class="[
              selectedPlan === plan.name
                ? 'ring-2 ring-primary border-primary elevation-6 transform scale-105'
                : 'elevation-2 hover:elevation-4'
            ]"
            @click="selectPlan(plan)"
            style="position: relative; border: 2px solid transparent"
            :style="selectedPlan === plan.name ? 'border-color: #1976D2;' : ''"
          >
            <!-- Checkbox/Selection Indicator -->
            <div
              v-if="selectedPlan === plan.name"
              class="absolute top-2 right-2"
              style="position: absolute; top: 10px; right: 10px"
            >
              <v-icon color="primary">mdi-check-circle</v-icon>
            </div>

            <h3 class="font-bold text-xl mb-2">{{ plan.name }}</h3>
            <div class="text-body-2 text-grey-darken-1 font-weight-medium">
              {{ plan.name === 'Base' ? '(Free)' : plan.users }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <div class="text-center mb-6">
        <a
          href="https://cualitywork.com/"
          target="_blank"
          class="text-decoration-none text-body-2 font-weight-bold d-inline-flex align-center hover:opacity-80"
          style="color: #1976d2"
        >
          View Plan Details
          <v-icon small color="primary" class="ml-1">mdi-open-in-new</v-icon>
        </a>
      </div>

      <div class="d-flex justify-end mt-8">
        <v-btn color="primary" @click="validatePlanAndProceed" depressed :block="$vuetify.breakpoint.smAndDown">
          {{ checkPlanButtonText }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Step 4: Set Passcode -->
    <div
      v-if="currentStep === 'set-passcode' && !googleSignupData"
      key="set-passcode"
      class="signup-step"
    >
      <div class="mb-6">
        <v-icon class="mr-2" size="30" @click="currentStep = 'plan-selection'"
          >mdi-chevron-left</v-icon
        >
        <span>Step-4</span>
      </div>
      <div class="mb-6">
        <h4 class="font-bold text-2xl mb-2">Set Your Passcode</h4>
        <p class="text-grey-darken-1">Create a secure 6-digit passcode</p>
      </div>

      <v-form ref="passcodeForm">
        <div class="mb-6">
          <label class="text-body-2 font-weight-medium mb-2 d-block">New Passcode</label>
          <PasscodeInput
            v-model="form.setPasscode"
            :autoFocus="true"
            @keyup.enter.native="handleFinalizeSignup"
          />
        </div>

        <div class="mb-6">
          <label class="text-body-2 font-weight-medium mb-2 d-block">Confirm Passcode</label>
          <PasscodeInput
            v-model="form.confirmPasscode"
            @keyup.enter.native="handleFinalizeSignup"
          />
        </div>

        <div class="d-flex justify-end mt-6">
          <v-btn
            color="primary"
            @click="handleFinalizeSignup"
            :loading="isLoading || isPaymentProcessing"
            :disabled="!canSetPasscode"
            depressed
            :block="$vuetify.breakpoint.smAndDown"
          >
            {{ selectedPlan === 'Base' ? 'Create Account' : 'Pay & Create Account' }}
            <v-icon right>mdi-account-check</v-icon>
          </v-btn>
        </div>
      </v-form>
    </div>

    <!-- Always available Razorpay Component (Invisible) -->
    <RazorpayCheckout
      ref="razorpayCheckout"
      :planUserName="`${form.firstName} ${form.lastName}`"
      :planUserEmail="form.email"
      :planUserContact="form.mobile"
      @success="handlePaymentSuccess"
      @error="handlePaymentFailure"
      @closed="isPaymentProcessing = false"
    />

    <!-- Toast Messages -->
    <div v-for="(alert, index) in alerts" :key="index" class="mt-4">
      <v-alert dense dismissible shaped text :type="alert.type" @input="removeAlert(index)">
        {{ alert.message }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import PasscodeInput from '@/components/PasscodeInput.vue'
import { maskPhoneNumber } from '@/utils/common'
import { debounce } from '@/utils/common'
import Loader from '@/components/UI/Loader.vue'
import GoogleLogin from '@/components/GoogleLogin.vue'

export default {
  name: 'SignupComponent',
  components: {
    PasscodeInput,
    Loader,
    GoogleLogin,
    RazorpayCheckout: () => import('@/components/common/RazorpayCheckout.vue')
  },
  data() {
    return {
      currentStep: 'user', //  user,company, otp, set-passcode
      isLoading: false,
      otpCounter: 0,
      otpTimer: null,
      mobileChecking: false,
      mobileChecked: false,
      mobileAvailable: true,
      orgChecking: false,
      orgChecked: false,
      orgAvailable: true,
      orgNameChecking: false,
      orgNameChecked: false,
      orgNameAvailable: true,
      steps: [
        { key: 'user', label: 'User Info' },
        { key: 'company', label: 'Company Info' },
        { key: 'plan-selection', label: 'Select Plan' },
        { key: 'set-passcode', label: 'Set Passcode' }
      ],
      form: {
        companyName: '',
        companyCode: '',
        companySize: '',
        useCase: ['hrms'],
        firstName: '',
        lastName: '',
        mobile: '',
        email: '',
        employeeCode: 'ADMIN1',
        otp: '',
        setPasscode: '',
        confirmPasscode: ''
      },
      selectedPlan: 'Base', // Default plan
      isPaymentProcessing: false,
      plans: [
        {
          name: 'Base',
          price: 0,
          users: '10 Users',
          description: 'Free'
        },
        {
          name: 'Silver',
          price: 299,
          users: 'Up to 50 Users'
        },
        {
          name: 'Gold',
          price: 499,
          users: 'Up to 200 Users'
        },
        {
          name: 'Diamond',
          price: 599,
          users: 'Unlimited Users'
        }
      ],
      alerts: [],
      companySizes: ['Less than 25', '25-50', '51-100', 'More than 100'],
      useCases: [
        {
          text: 'HRMS',
          value: 'hrms',
          description: 'Manage and track HR processes'
        },
        {
          text: 'BASE',
          value: 'base',
          description: 'Manage and track base processes'
        },
        {
          text: 'Collection Management',
          value: 'collection_management',
          description: 'Manage and track debt collection processes'
        },
        {
          text: 'Loan Management',
          value: 'loan_management',
          description: 'Handle loan applications, disbursements, and repayments'
        }
      ],
      rules: {
        required: (value) => !!value || 'This field is required',
        companyCode: (value) =>
          (value &&
            value.length === 6 &&
            value === value.toUpperCase() &&
            /^[A-Z]+$/.test(value)) ||
          'Code must be 6 uppercase letters only',
        mobile: (value) =>
          (value && value.length === 10 && /^\d+$/.test(value)) ||
          'Enter a valid 10-digit mobile number'
      },
      googleSignupData: null // To store Google user data during signup
    }
  },
  computed: {
    checkPlanButtonText() {
      if (this.selectedPlan === 'Base') return 'Create Account'
      return this.googleSignupData?.googleToken ? 'Pay & Create Account' : 'Create Account'
    },
    isCompanyFormValid() {
      return (
        this.form.companyName &&
        this.orgNameAvailable &&
        this.form.companyCode &&
        this.form.companyCode.length === 6 &&
        this.form.companyCode === this.form.companyCode.toUpperCase() &&
        /^[A-Z]+$/.test(this.form.companyCode) &&
        this.orgAvailable &&
        this.form.companySize &&
        this.form.companySize &&
        this.form.useCase &&
        this.form.useCase.length >= 2
      )
    },
    isUserFormValid() {
      if (this.googleSignupData && this.googleSignupData.email) return true
      return (
        this.form.firstName &&
        this.form.lastName &&
        this.form.mobile &&
        this.form.mobile.length === 10 &&
        /^\d+$/.test(this.form.mobile) &&
        this.form.employeeCode
      )
    },
    canSetPasscode() {
      if (
        this.form.setPasscode?.length === 6 &&
        this.form.confirmPasscode?.length === 6 &&
        this.form.setPasscode !== this.form.confirmPasscode
      ) {
        this.showToast('Passcodes do not match', 'error')
      }
      return (
        this.form.setPasscode &&
        this.form.confirmPasscode &&
        this.form.setPasscode.length === 6 &&
        this.form.setPasscode === this.form.confirmPasscode
      )
    }
  },
  created() {
    // Create debounced functions
    this.debouncedCheckMobileAvailability = debounce(this.checkMobileAvailability, 500)
    this.debouncedCheckOrgAvailability = debounce(this.checkOrgAvailability, 500)
    this.debouncedCheckOrgNameAvailability = debounce(this.checkOrgNameAvailability, 500)
  },
  methods: {
    maskNumber(num) {
      return maskPhoneNumber(String(num))
    },
    handleCompanyCodeInput() {
      // Remove any numbers from the input and convert to uppercase
      this.form.companyCode = this.form.companyCode.replace(/[^A-Za-z]/g, '').toUpperCase()

      if (this.form.companyCode && this.form.companyCode.length === 6) {
        this.debouncedCheckOrgAvailability()
      }
    },

    handleUseCaseChange(val) {
      if (!val.includes('hrms')) {
        this.form.useCase = ['hrms', ...val]
      }
    },

    handleCompanyNameInput() {
      if (this.form.companyName) {
        this.debouncedCheckOrgNameAvailability()
        // Also regenerate company code
        this.generateCompanyCode()
      }
    },

    generateCompanyCode() {
      if (this.form.companyName) {
        this.form.companyCode = this.form.companyName
          .replace(/\s+/g, '')
          .substring(0, 6)
          .toUpperCase()
          .replace(/[^A-Z]/g, '') // Only keep letters, remove numbers
      } else {
        this.form.companyCode = ''
      }
      // Check org availability when company code changes
      if (this.form.companyCode && this.form.companyCode.length === 6) {
        this.debouncedCheckOrgAvailability()
      }
    },

    async checkOrgNameAvailability() {
      if (!this.form.companyName) return

      this.orgNameChecking = true
      this.orgNameChecked = false
      this.orgNameAvailable = true // Reset to default

      try {
        const res = await this.$axios.post('/cualitywork/orgname-availability', {
          name: this.form.companyName
        })
        this.orgNameAvailable = res.data?.available
        this.orgNameChecked = true

        if (!this.orgNameAvailable) {
          this.showToast('This organization name is already taken', 'error')
        }
      } catch (error) {
        this.orgNameAvailable = error.response?.data?.available || false
        this.orgNameChecked = true
      } finally {
        this.orgNameChecking = false
      }
    },

    async checkOrgAvailability() {
      if (!this.form.companyCode || this.form.companyCode.length !== 6) return

      this.orgChecking = true
      this.orgChecked = false
      this.orgAvailable = true // Reset to default

      try {
        const res = await this.$axios.post('/cualitywork/orgcode-availability', {
          code: this.form.companyCode
        })
        this.orgAvailable = res.data?.available
        this.orgChecked = true

        if (!this.orgAvailable) {
          this.showToast('This organization code is already taken', 'error')
        }
      } catch (error) {
        this.orgAvailable = error.response?.data?.available || false
        this.orgChecked = true
      } finally {
        this.orgChecking = false
      }
    },

    async checkMobileAvailability() {
      // Reset mobile validation state
      this.mobileChecked = false
      this.mobileAvailable = true // Reset to default

      // Validate mobile number first
      if (!this.form.mobile || this.form.mobile.length !== 10 || !/^\d+$/.test(this.form.mobile)) {
        return
      }

      this.mobileChecking = true

      try {
        const res = await this.$axios.post('/cualitywork/mobile-availability', {
          mobile: Number(this.form.mobile)
        })

        this.mobileAvailable = res.data?.available

        this.mobileChecked = true

        if (!this.mobileAvailable) {
          this.showToast('This mobile number is already registered', 'error')
        }
      } catch (error) {
        this.mobileAvailable = error.response?.data?.available || false
        this.mobileChecked = true
      } finally {
        this.mobileChecking = false
      }
    },
    checkNumberValidity() {
      this.form.mobile = this.form.mobile.replace(/\D/g, '').slice(0, 10)
    },
    async validateCompanyAndNext() {
      if (!this.isCompanyFormValid) {
        this.showToast('Please fill all required company information', 'error')
        return
      }

      if (!this.orgNameAvailable) {
        this.showToast('Please use a different organization name', 'error')
        return
      }

      if (!this.orgAvailable) {
        this.showToast('Please use a different organization code', 'error')
        return
      }

      this.currentStep = 'plan-selection'
    },
    async validatePlanAndProceed() {
      try {
        if (this.googleSignupData && this.googleSignupData?.googleToken) {
          console.log('Google signup')
          await this.checkPlanAndProceed()
        } else {
          this.currentStep = 'set-passcode'
        }
      } catch (error) {
        console.error(error)
        this.showToast('Something went wrong', 'error')
      }
    },
    handlePaymentSuccess(response) {
      console.log('Payment success', response)
      this.isPaymentProcessing = false
      if (response && response.status !== 'skipped') {
        this.showToast('Payment successful', 'success')
      }
      this.executeCompleteSignup()
    },
    handlePaymentFailure(error) {
      console.error('Payment failed', error)
      this.isPaymentProcessing = false
      this.showToast('Payment failed/cancelled. Please try again.', 'error')
    },
    async handleFinalizeSignup() {
      if (!this.canSetPasscode) {
        return
      }

      // First set passcode
      await this.setNewPasscode()

      await this.checkPlanAndProceed()
    },
    async checkPlanAndProceed() {
      // Logic is now centralized in executeCompleteSignup handling both correct order of operations
      await this.executeCompleteSignup()
    },
    selectPlan(plan) {
      this.selectedPlan = plan.name
    },
    async validateUserAndProceed() {
      if (!this.isUserFormValid) {
        this.showToast('Please fill all required user information', 'error')
        return
      }

      if (!this.mobileAvailable && !(this.googleSignupData && this.googleSignupData?.email)) {
        this.showToast('Please use a different mobile number', 'error')
        return
      }

      this.currentStep = 'company'
    },
    startOtpCounter() {
      this.otpCounter = 60
      clearInterval(this.otpTimer)
      this.otpTimer = setInterval(() => {
        if (this.otpCounter > 0) {
          this.otpCounter--
        } else {
          clearInterval(this.otpTimer)
        }
      }, 1000)
    },
    // resendOTP() {
    //   if (this.otpCounter > 0) return
    //
    //   this.isLoading = true
    //   this.sendOTPApi()
    //     .then(() => {
    //       this.isLoading = false
    //       this.startOtpCounter()
    //       this.showToast('OTP sent to your mobile number', 'success')
    //     })
    //     .catch((error) => {
    //       this.isLoading = false
    //       this.showToast(error.message || 'Failed to send OTP. Please try again.', 'error')
    //     })
    // },
    // verifyOTP() {
    //   if (!this.form.otp || this.form.otp.length !== 4) {
    //     this.showToast('Please enter a valid 4-digit OTP', 'error')
    //     return
    //   }
    //
    //   this.isLoading = true
    //   // Use the actual API to verify OTP
    //   this.verifyOTPApi()
    //     .then((isValid) => {
    //       if (isValid) {
    //         this.isLoading = false
    //         this.currentStep = 'set-passcode'
    //         this.showToast('OTP verified successfully', 'success')
    //       } else {
    //         this.isLoading = false
    //         this.showToast('Invalid OTP. Please try again.', 'error')
    //       }
    //     })
    //     .catch((error) => {
    //       this.isLoading = false
    //       this.showToast(error.message || 'Failed to verify OTP. Please try again.', 'error')
    //     })
    // },
    async setPasscode() {
      if (!this.canSetPasscode) {
        return
      }

      this.isLoading = true
      await this.setNewPasscode()
      // Execute the complete signup process including OTP verification
      await this.executeCompleteSignup()
    },
    // async validatePasscodeAndNext() {
    //   if (!this.canSetPasscode) {
    //     return
    //   }
    //
    //   this.isLoading = true
    //   await this.setNewPasscode()
    //   this.isLoading = false
    //   this.currentStep = 'payment'
    // },

    async setNewPasscode() {
      if (this.form.setPasscode !== this.form.confirmPasscode) {
        this.showToast('Passcodes do not match', 'error')
        return
      }

      if (this.form.setPasscode.length !== 6) {
        this.showToast('Passcode must be 6 digits', 'error')
        return
      }

      // Store passcode temporarily - will be set during login
      this.form.passCode = this.form.setPasscode
      this.form.setPasscode = null
      this.form.confirmPasscode = null
      return Promise.resolve()
    },

    // New method to handle the complete signup process
    async executeCompleteSignup() {
      try {
        this.isLoading = true

        const plan = this.plans.find((p) => p.name === this.selectedPlan)
        if (!plan) throw new Error('Invalid plan selected')

        let planId
        let organizationId
        let userId

        if (plan.price === 0) {
          planId = await this.createPlan()

          organizationId = await this.createOrganization(planId)

          userId = await this.createEmployee(organizationId)

          await this.assignRoleToEmployee(userId, organizationId)
        } else {
          this.isPaymentProcessing = true

          const isLoaded = await this.$refs.razorpayCheckout.loadRazorpay()
          if (!isLoaded)
            throw new Error('Razorpay SDK failed to load. Please check your internet connection.')

          const planAmount = plan.price * 100
          const orderResponse = await this.$axios.post('/cualitywork/create-signup-order', {
            amount: planAmount,
            currency: 'INR',
            orgName: this.form.companyName || 'New Company'
          })

          if (!orderResponse.data.success) {
            throw new Error('Failed to create payment order')
          }
          const { order } = orderResponse.data

          await new Promise((resolve, reject) => {
            const options = {
              key: 'rzp_test_RsjI2YFWzhYic2',
              amount: order.amount,
              currency: order.currency,
              name: 'CualityWork',
              description: `Subscription for ${plan.name}`,
              order_id: order.id,
              prefill: {
                name: this.form.firstName + ' ' + this.form.lastName,
                email: this.form.email,
                contact: this.form.mobile
              },
              theme: { color: '#3399cc' },
              handler: async (response) => {
                try {
                  const customerRes = await this.$axios.post(
                    '/cualitywork/create-signup-customer',
                    {
                      orgName: this.form.companyName,
                      contactName: this.form.firstName + ' ' + this.form.lastName,
                      contactEmail: this.form.email,
                      contactMobile: this.form.mobile
                    }
                  )
                  const newCustomerId = customerRes.data.customerId

                  //org creation and its link to plan - START
                  planId = await this.createPlan()
                  organizationId = await this.createOrganization(planId)
                  userId = await this.createEmployee(organizationId)
                  await this.assignRoleToEmployee(userId, organizationId)
                  //org creation and its link to plan - END

                  await this.$axios.post('/cualitywork/verify-signup-payment', {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    orgId: organizationId,
                    planId: planId,
                    amount: planAmount,
                    customerId: newCustomerId
                  })

                  resolve()
                } catch (err) {
                  reject(new Error('Post-payment setup failed: ' + err.message))
                }
              },
              modal: {
                ondismiss: function () {
                  reject(new Error('Payment cancelled by user'))
                }
              }
            }

            const rzp1 = new window.Razorpay(options)
            rzp1.on('payment.failed', function (response) {
              reject(new Error(response.error.description))
            })
            rzp1.open()
          })
          this.isPaymentProcessing = false
        }

        // return

        await this.handleSubmitQuery(organizationId)

        this.showToast('Account created successfully! Logging you in...', 'success')

        localStorage.setItem('needsInitialSetup', 'true')
        const selectedUseCases = this.form.useCase.filter((uc) => uc !== 'hrms')
        localStorage.setItem('setupUseCase', JSON.stringify(selectedUseCases))

        // Automatically login the user after successful signup
        if (this.googleSignupData && this.googleSignupData.googleToken) {
          await this.loginGoogleUser()
        } else {
          await this.loginUser()
        }
        this.isLoading = false
      } catch (error) {
        console.error('Signup process failed:', error)
        this.isLoading = false
        this.isPaymentProcessing = false
        this.showToast(
          error.message || 'Failed to complete signup process. Please try again.',
          'error'
        )
      }
    },
    async loginGoogleUser() {
      try {
        // Emit event to parent component to handle login with Google token
        this.$emit('google-login', {
          token: this.googleSignupData?.googleToken,
          user: { name: this.googleSignupData?.name, email: this.googleSignupData?.email }
        })
      } catch (error) {
        console.error('Login failed:', error)
        this.showToast('Failed to login. Please try logging in manually.', 'error')
      }
    },
    // New method to login the user after successful signup
    async loginUser() {
      try {
        // Emit event to parent component to handle login
        this.$emit('signup-complete', {
          mobile: this.form.mobile,
          passcode: this.form.passCode,
          organization_code: this.form.companyCode
        })
      } catch (error) {
        console.error('Login failed:', error)
        this.showToast('Failed to login. Please try logging in manually.', 'error')
      }
    },

    // API call to verify OTP
    // async verifyOTPApi() {
    //   try {
    //     const response = await this.$axios.post('/auth/otp/verify', {
    //       mobile: this.form.mobile,
    //       otp: this.form.otp,
    //       organization_code: this.form.companyCode
    //     })
    //     return response.data?.access_token === true
    //   } catch (error) {
    //     console.error('OTP verification failed:', error)
    //     throw new Error('Invalid OTP. Please try again.')
    //   }
    // },

    // API call to send OTP
    // async sendOTPApi() {
    //   try {
    //     await this.$axios.post('/auth/otp', {
    //       mobile: Number(this.form.mobile)
    //     })
    //     return true
    //   } catch (error) {
    //     console.error('Failed to send OTP:', error)
    //     throw new Error('Failed to send OTP. Please try again.')
    //   }
    // },

    // Step 1: Create plan and return plan_id
    async createPlan() {
      try {
        const planResponse = await this.$axios.post('/cualitywork/open-plan', {
          planName: `${this.form.companyName} Plan`,
          planDescription: `Default plan for ${this.form.companyName}`,
          useCase: this.form.useCase?.[1],
          selectMonths: 12
        })
        return planResponse.data?.plan?.plan_id
      } catch (error) {
        console.error('Failed to create plan:', error)
        throw new Error('Failed to create plan')
      }
    },

    // Step 2: Create organization with plan_id and return organization_id
    async createOrganization(planId) {
      try {
        const now = new Date()
        const nextMonth = new Date()
        nextMonth.setMonth(now.getMonth() + 1)
        
        const validFrom = now.toISOString().split('T')[0]
        const validTo = nextMonth.toISOString().split('T')[0]

        const orgResponse = await this.$axios.post('/cualitywork/open-org', {
          name: this.form.companyName,
          code: this.form.companyCode,
          valid_from: validFrom,
          valid_to: validTo,
          // size: this.form.companySize,
          plan_id: planId
        })
        return orgResponse.data?.organization?.organization_id
      } catch (error) {
        console.error('Failed to create organization:', error)
        throw new Error('Failed to create organization')
      }
    },

    // Step 3: Create employee with organization_id and return user_id
    async createEmployee(organizationId) {
      try {
        const payload = {
          first_name: this.form?.firstName || this.googleSignupData?.firstName,
          last_name: this.form?.lastName || this.googleSignupData?.lastName || undefined,
          mobile: Number(this.form?.mobile) || undefined,
          email: this.form?.email || this.googleSignupData?.email || undefined,
          employee_id: this.form?.employeeCode || 'ADMIN1',
          organization_id: organizationId,
          passcode: this.form?.passCode || undefined,
          is_google_logged_in: this.googleSignupData?.googleToken ? 1 : undefined,
          is_email_verified: this.googleSignupData?.email ? 1 : undefined
        }
        const employeeResponse = await this.$axios.post('/cualitywork/open-employee', payload)
        return employeeResponse.data?.data?.systemUserId
      } catch (error) {
        console.error('Failed to create employee:', error)
        throw new Error('Failed to create employee')
      }
    },

    // Step 4: Assign WFM_ADMIN role to employee
    async assignRoleToEmployee(userId, organizationId) {
      try {
        await this.$axios.post(`/cualitywork/assign-role/${userId}`, {
          organization_id: organizationId
        })
      } catch (error) {
        console.error('Failed to assign role to employee:', error)
        throw new Error('Failed to assign role to employee')
      }
    },
    async handleSubmitQuery(orgId) {
      try {
        const formData = new FormData()
        const details = `New Organization created successfully for ${this.form?.companyName} with code ${this.form?.companyCode} and employee code ${this.form?.employeeCode}. Size of company is ${this.form?.companySize}.`
        // this.attachments.forEach((file) => {
        //   formData.append('attachments', file)
        // })
        const fullName = this.form?.firstName
          ? `${this.form?.firstName} ${this.form?.lastName || ''}`
          : this.googleSignupData?.name
        const email = this.form?.email || this.googleSignupData?.email
        const mobile = this.form?.mobile || 'N/A'
        formData.append('typeOfQuery', 'NEW_ORG_SETUP')
        formData.append('details', details)
        formData.append('companyName', this.form.companyName)
        formData.append('contactPerson', fullName)
        formData.append('contactEmail', email)
        formData.append('contactNumber', mobile)
        formData.append('sizeOfCompany', this.form.companySize)
        formData.append('useCase', this.form.useCase?.[1])
        formData.append('orgId', orgId)
        const response = await this.$axios.post('/cualitywork/query', formData)

        if (response.data.success) {
          this.$toast.success(response?.data?.message)
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Something went wrong'
        this.$toast.error(errorMessage)
      }
    },
    async handleGoogleLoginSuccess(googleResponse) {
      const { token, user } = googleResponse
      // console.log('Google login successful', googleResponse)
      const checkUser = await this.checkIsUserExist(user.email)

      if (checkUser) {
        this.$emit('google-login', googleResponse)
      } else {
        this.googleSignupData = {
          name: user.name,
          email: user.email,
          firstName: user?.given_name,
          lastName: user?.family_name,
          googleToken: token
        }
        await this.validateUserAndProceed()
      }
    },
    async checkIsUserExist(email) {
      try {
        const response = await this.$axios.post('/cualitywork/ext-user-details', {
          username: email
        })
        return response?.status ? true : false
      } catch (error) {
        console.error('Error checking user exist:', error)
        return false
      }
    },

    showToast(message, type = 'info') {
      const alert = { message, type }
      this.alerts.push(alert)
      setTimeout(() => {
        this.removeAlert(this.alerts.indexOf(alert))
      }, 5000)
    },
    removeAlert(index) {
      if (index > -1) {
        this.alerts.splice(index, 1)
      }
    }
  },
  beforeDestroy() {
    clearInterval(this.otpTimer)
  }
}
</script>

<style scoped>
.signup-step {
  position: relative;
  padding: 8px 0;
}

.text-grey-darken-1 {
  color: #757575;
}
</style>
